import React, { useState, isValidElement, useEffect } from 'react'
import Icon from '../common/Icon'
import Loading from '../common/Loading'
import Avatar from '../common/Avatar'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCroissant } from '@fortawesome/pro-solid-svg-icons'
import { User } from '../../context/AuthContext'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter'
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism'
import {
  Clipboard,
  Check,
  Copy,
  FileText,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react'
import {
  DocumentReference,
  DaxQuery,
  addMessageFeedback,
  FeedbackType,
} from '../../api/chatService'
import DaxQueriesBox from './DaxQueriesBox'
import ChartBlock from './chart/ChartBlock'

export type MessageRole = 'user' | 'assistant' | 'error'

interface MessageProps {
  role: MessageRole
  content: string
  isLoading?: boolean
  user: User
  documents?: DocumentReference[]
  daxQueries?: DaxQuery[]
  streamingComplete?: boolean
  messageId?: string
  chatId?: string
  feedback?: FeedbackType | null
  onFeedback?: (messageId: string, feedback: FeedbackType | null) => void
}

const Message: React.FC<MessageProps> = ({
  role,
  content,
  isLoading = false,
  user,
  documents,
  daxQueries,
  streamingComplete = true,
  messageId,
  chatId,
  feedback,
  onFeedback,
}) => {
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({})
  const [messageCopied, setMessageCopied] = useState(false)
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackType | null>(
    null
  )

  useEffect(() => {
    setSelectedFeedback(feedback || null)
  }, [feedback])

  const handleCopy = (code: string, id: string) => {
    navigator.clipboard.writeText(code)
    setCopiedStates(prev => ({ ...prev, [id]: true }))
    setTimeout(() => {
      setCopiedStates(prev => ({ ...prev, [id]: false }))
    }, 2000)
  }

  const handleMessageCopy = () => {
    navigator.clipboard.writeText(content)
    setMessageCopied(true)
    setTimeout(() => {
      setMessageCopied(false)
    }, 2000)
  }

  const handleFeedbackClick = async (newFeedbackType: FeedbackType) => {
    if (!messageId || !chatId) {
      console.warn('messageId or chatId is missing, cannot send feedback.')
      return
    }
    console.log('messageId', messageId)
    console.log('chatId', chatId)
    console.log('message', content)

    const finalFeedbackToSet =
      selectedFeedback === newFeedbackType ? null : newFeedbackType
    setSelectedFeedback(finalFeedbackToSet)

    try {
      if (finalFeedbackToSet !== null) {
        await addMessageFeedback(chatId, messageId, {
          type: finalFeedbackToSet,
        })
      } else {
        await addMessageFeedback(chatId, messageId, null)
      }
      if (onFeedback) {
        onFeedback(messageId, finalFeedbackToSet)
      }
    } catch (error) {
      console.error('Failed to submit feedback:', error)
      // Optionally revert UI state if API call fails
      // setSelectedFeedback(selectedFeedback); // Revert to previous state
    }
  }

  const gailsRedColor = '#cf2330'

  return (
    <div className={`message message--${role}`}>
      <div className="message__container">
        <div className={`message__avatar message__avatar--${role}`}>
          {role === 'user' ? (
            <Avatar user={user} size={24} className="message__avatar-user" />
          ) : role === 'error' ? (
            <Icon
              name="alert-triangle"
              size={24}
              className="message__avatar-error"
            />
          ) : (
            <FontAwesomeIcon
              icon={faCroissant}
              className="message__avatar-croissant"
            />
          )}
        </div>
        <div className="message__content">
          {isLoading ? (
            <Loading />
          ) : role === 'assistant' ? (
            <div className="message__markdown">
              <Markdown
                remarkPlugins={[remarkGfm]}
                components={{
                  pre: ({ node, ...props }) => {
                    let codeElement: React.ReactElement<{
                      className?: string
                      children?: React.ReactNode | React.ReactNode[]
                    }> | null = null

                    if (isValidElement(props.children)) {
                      // Handle case where props.children is a single React element (should be <code>)
                      // This might not be typical for `pre` but good to cover
                      if (props.children.type === 'code') {
                        codeElement = props.children as React.ReactElement<{
                          className?: string
                          children?: React.ReactNode | React.ReactNode[]
                        }>
                      }
                    } else if (
                      Array.isArray(props.children) &&
                      props.children.length > 0 &&
                      isValidElement(props.children[0]) &&
                      props.children[0].type === 'code'
                    ) {
                      // Typical case: props.children is an array, and the first element is the <code> element
                      codeElement = props.children[0] as React.ReactElement<{
                        className?: string
                        children?: React.ReactNode | React.ReactNode[]
                      }>
                    }

                    if (!codeElement) {
                      // Fallback for unexpected structure or if no <code> element is found
                      return <pre {...props} />
                    }

                    let codeString = ''
                    if (codeElement.props.children) {
                      if (Array.isArray(codeElement.props.children)) {
                        codeString = codeElement.props.children
                          .map(child => String(child))
                          .join('')
                      } else {
                        codeString = String(codeElement.props.children)
                      }
                    }
                    codeString = codeString.replace(/\n$/, '')

                    const language =
                      codeElement.props.className?.replace('language-', '') ||
                      'text'

                    // ```chart blocks render as an inline chart instead of code.
                    if (language === 'chart') {
                      return <ChartBlock code={codeString} />
                    }

                    const blockKey = (
                      props.key ||
                      node?.position?.start.line ||
                      Math.random()
                    ).toString()
                    const uniqueBlockId = `code-block-${blockKey}-${language}`

                    return (
                      <pre className="code-block">
                        <div className="code-block__header">
                          <span className="code-block__language">
                            {language}
                          </span>
                          <button
                            className="code-block__copy-button"
                            onClick={() =>
                              handleCopy(codeString, uniqueBlockId)
                            }
                          >
                            {copiedStates[uniqueBlockId] ? (
                              <Check size={14} />
                            ) : (
                              <Clipboard size={14} />
                            )}
                            {copiedStates[uniqueBlockId] ? 'Copied!' : 'Copy'}
                          </button>
                        </div>
                        <SyntaxHighlighter
                          style={vscDarkPlus}
                          language={language}
                          PreTag="div"
                        >
                          {codeString}
                        </SyntaxHighlighter>
                      </pre>
                    )
                  },
                }}
              >
                {content}
              </Markdown>
            </div>
          ) : role === 'error' ? (
            <p className="message__error-text">{content}</p>
          ) : (
            <div className="message__content-text">
              <p>{content}</p>
            </div>
          )}

          {!isLoading &&
            role === 'assistant' &&
            streamingComplete &&
            daxQueries &&
            daxQueries.length > 0 && <DaxQueriesBox daxQueries={daxQueries} />}

          {!isLoading && role === 'assistant' && streamingComplete && (
            <div
              className={`message__copy-container ${
                documents && documents.length > 0
                  ? 'message__copy-container--with-docs'
                  : 'message__copy-container--no-docs'
              }`}
            >
              {documents && documents.length > 0 && (
                <div className="message__documents">
                  {documents.map((doc, index) => (
                    <div key={index} className="message__document-item">
                      <FileText size={14} />
                      <div className="message__document-info">
                        {doc.web_url ? (
                          <a
                            href={doc.web_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="message__document-link"
                          >
                            {doc.filename}
                          </a>
                        ) : (
                          <span className="message__document-name">
                            {doc.filename}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="message__actions">
                <button
                  className="message__icon-button message__copy-button"
                  onClick={handleMessageCopy}
                  title="Copy message"
                >
                  {messageCopied ? <Check size={16} /> : <Copy size={16} />}
                </button>

                <button
                  className="message__icon-button message__feedback-button"
                  onClick={() => handleFeedbackClick(FeedbackType.POSITIVE)}
                  title="Good response"
                  style={{
                    color:
                      selectedFeedback === FeedbackType.POSITIVE
                        ? gailsRedColor
                        : undefined,
                  }}
                >
                  <ThumbsUp size={16} />
                </button>
                <button
                  className="message__icon-button message__feedback-button"
                  onClick={() => handleFeedbackClick(FeedbackType.NEGATIVE)}
                  title="Bad response"
                  style={{
                    color:
                      selectedFeedback === FeedbackType.NEGATIVE
                        ? gailsRedColor
                        : undefined,
                  }}
                >
                  <ThumbsDown size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Message
